import { useState, useCallback, useEffect } from 'react';
export type StorageType = 'localStorage' | 'sessionStorage';

/** Same-tab change notifications. The native `storage` event only fires in
 *  *other* tabs, so every write dispatches one of these for listeners in the
 *  writing tab. Anything that writes to these stores outside `createStorage`
 *  — the auth token store, for one — must dispatch the matching event or
 *  React state silently goes stale. */
export const localStorageEvent = 'tixly-local-storage';
export const sessionStorageEvent = 'tixly-session-storage';
export interface IStorageProperties<T> {
  /** Storage key */
  key: string;

  /** Default value that will be set if value is not found in storage */
  defaultValue?: T;

  /** If set to true, value will be update is useEffect after mount */
  getInitialValueInEffect?: boolean;

  /** Function to serialize value into string to be save in storage */
  serialize?(value: T): string;

  /** Function to deserialize string value from storage to value */
  deserialize?(value: string): T;
}
function serializeJSON<T>(value: T, hookName: string) {
  try {
    return JSON.stringify(value);
  } catch (error) {
    throw new Error(`@mantine/hooks ${hookName}: Failed to serialize the value`);
  }
}
function deserializeJSON(value: string) {
  try {
    return JSON.parse(value);
  } catch {
    return value;
  }
}
export function useWindowEvent<K extends string>(type: K, listener: K extends keyof WindowEventMap ? (this: Window, ev: WindowEventMap[K]) => void : (this: Window, ev: CustomEvent) => void, options?: boolean | AddEventListenerOptions) {
  useEffect(() => {
    (window as any).addEventListener(type, listener, options);
    return () => (window as any).removeEventListener(type, listener, options);
  }, []);
}
export function createStorage<T>(type: StorageType, hookName: string) {
  const eventName = type === 'localStorage' ? localStorageEvent : sessionStorageEvent;
  return function useStorage({
    key,
    defaultValue = undefined,
    getInitialValueInEffect = true,
    deserialize = deserializeJSON,
    serialize = (value: T) => serializeJSON(value, hookName)
  }: IStorageProperties<T>) {
    const readStorageValue = useCallback((skipStorage?: boolean): T => {
      if (typeof window === 'undefined' || !(type in window) || skipStorage) {
        return (defaultValue ?? '') as T;
      }
      const storageValue = window[type].getItem(key);
      return storageValue !== null ? deserialize(storageValue) : (defaultValue ?? '') as T;
    }, [key, defaultValue]);
    const [value, setValue] = useState<T>(readStorageValue(getInitialValueInEffect));
    const setStorageValue = useCallback((val: T | ((prevState: T) => T)) => {
      if (val instanceof Function) {
        setValue(current => {
          const result = val(current);
          window[type].setItem(key, serialize(result));
          window.dispatchEvent(new CustomEvent(eventName, {
            detail: {
              key,
              value: val(current)
            }
          }));
          return result;
        });
      } else {
        window[type].setItem(key, serialize(val));
        window.dispatchEvent(new CustomEvent(eventName, {
          detail: {
            key,
            value: val
          }
        }));
        setValue(val);
      }
    }, [key]);
    const setValueIfChanged = (next: T) => setValue(current => serialize(current) === serialize(next) ? current : next);
    useWindowEvent('storage', event => {
      if (event.storageArea === window[type] && event.key === key) {
        setValueIfChanged(deserialize(event.newValue ?? ''));
      }
    });
    useWindowEvent(eventName, event => {
      if (event.detail.key === key) {
        setValueIfChanged(event.detail.value);
      }
    });
    useEffect(() => {
      if (defaultValue !== undefined && value === undefined) {
        setStorageValue(defaultValue);
      }
    }, [defaultValue, value, setStorageValue]);
    useEffect(() => {
      if (getInitialValueInEffect) {
        setValue(readStorageValue());
      }
    }, [key]);
    return [value === undefined ? defaultValue : value, setStorageValue] as const;
  };
}
export function useLocalStorage<T = string>(props: IStorageProperties<T>) {
  return createStorage<T>('localStorage', 'use-local-storage')(props);
}
export function useSessionStorage<T = string>(props: IStorageProperties<T>) {
  return createStorage<T>('sessionStorage', 'use-session-storage')(props);
}